import { NavigationModule } from './types';

/**
 * Role constants matching the Roles class from components/roles/index.jsx
 * Used for module and page-level access control.
 */
const ROLES = {
  /** Operator (Super Admin) - cross-company access */
  OPERATOR: ['Operator'],
  /** HR and Admin roles - company-wide access */
  HR_AND_ADMIN: ['Hr', 'Admin'],
  /** Manager roles - team-level access */
  MANAGER: ['Manager', 'Hr', 'Chief'],
  /** Clocking roles - all logged-in users except Operator */
  CLOCKING: ['Clocking', 'Hr', 'Chief', 'Manager', 'Admin'],
  /** Settings access - HR, Admin, and Operator */
  SETTINGS: ['Hr', 'Admin', 'Operator'],
  /** HR-only access */
  HR_ONLY: ['Hr'],
  /** HR and Operator access (e.g. Compliance Config) */
  HR_AND_OPERATOR: ['Hr', 'Operator'],
};

/**
 * Navigation module configuration for the sidebar.
 * Defines all modules with their pages, icons, colors, and role-based access.
 *
 * All paths correspond to actual routes defined in App.tsx.
 * Coming Soon pages are placed inside their parent modules with comingSoon: true.
 *
 * Mapping validated against: .planning/phases/08.2-navigation-revamp/routes mapping.md
 */
/**
 * Profile tab configuration for the UserProfile page.
 * Used by ModuleConfig to let operators enable/disable profile tabs per company.
 */
export const PROFILE_TAB_MODULE_ID = 'profile-tabs';

export interface ProfileTab {
  id: string;
  title: string;
  icon: string;
  alwaysEnabled?: boolean;
}

export const profileTabs: ProfileTab[] = [
  { id: 'personal', title: 'Personal', icon: 'User', alwaysEnabled: true },
  { id: 'documents', title: 'Documents', icon: 'FileText' },
  { id: 'skills', title: 'Skills', icon: 'Award' },
  { id: 'payroll', title: 'Payroll', icon: 'DollarSign' },
  { id: 'attendance', title: 'Attendance', icon: 'Clock' },
  { id: 'map', title: 'Workplace Map', icon: 'MapPin' },
  { id: 'assets', title: 'Assets', icon: 'Laptop' },
  { id: 'businessRoles', title: 'Business Roles', icon: 'UserCog' },
  { id: 'dependents', title: 'Dependents', icon: 'Users' },
];

export const navigationModules: NavigationModule[] = [
  {
    id: 'home',
    title: 'Home',
    icon: 'Home',
    color: 'from-blue-500 to-blue-600',
    pages: [
      { title: 'Dashboard', path: '/', icon: 'LayoutDashboard' },
      { title: 'Pending Actions', path: '/pending-actions', icon: 'ClipboardCheck' }
    ]
  },
  {
    id: 'ai-consultant',
    title: 'AI Consultant',
    icon: 'Sparkles',
    color: 'from-violet-500 to-purple-600',
    roles: ROLES.HR_AND_ADMIN,
    pages: [
      { title: 'AI Implementation Consultant', path: '/ai-consultant', icon: 'Sparkles' },
    ]
  },
  {
    id: 'attendance',
    title: 'Time & Attendance',
    icon: 'Clock',
    color: 'from-blue-500 to-indigo-600',
    roles: ROLES.CLOCKING,
    pages: [
      { title: 'Clock In / Out', path: '/attendance', icon: 'Clock' },
      { title: 'Attendance Overview', path: '/attendance/overview', icon: 'Calendar' },
      { title: 'Team Attendance', path: '/attendance/team-attendance', icon: 'Users', roles: ROLES.MANAGER },
      { title: 'Work Schedules', path: '/attendance/work-schedules', icon: 'CalendarDays', roles: ROLES.MANAGER },
      { title: 'Shift Swaps', path: '/shift-swaps', icon: 'ArrowLeftRight' },
      { title: 'Maps', path: '/attendance/maps', icon: 'Map', roles: ROLES.MANAGER },
      { title: 'Correction Requests', path: '/attendance/correction-requests', icon: 'FileEdit' },
      { title: 'Colleagues Availability', path: '/attendance/colleagues-availability', icon: 'BarChart3' },
      { title: 'Reports', path: '/attendance/reports', icon: 'PieChart', roles: ROLES.MANAGER },
      // Coming Soon
      { title: 'Attendance Dashboard', path: '/attendance-dashboard', icon: 'BarChart3', comingSoon: true },
      { title: 'Duty Roster Management', path: '/duty-roster-management', icon: 'CalendarDays', comingSoon: true },
    ]
  },
  {
    id: 'leaves',
    title: 'Leaves',
    icon: 'Palmtree',
    color: 'from-green-500 to-emerald-600',
    roles: ROLES.CLOCKING,
    pages: [
      { title: 'My Leaves', path: '/my-leaves', icon: 'CalendarDays' },
      { title: 'Leave Approvals', path: '/leave-approvals', icon: 'ClipboardCheck', roles: ROLES.MANAGER },
      { title: 'Team Calendar', path: '/team-calendar', icon: 'CalendarDays', roles: ROLES.MANAGER },
      // { title: 'Return to Work', path: '/return-to-work', icon: 'ClipboardCheck' },
      // { title: 'Compensatory Off', path: '/comp-off', icon: 'Award' },
      // { title: 'Short Leave', path: '/short-leave', icon: 'Timer' },
      // { title: 'Leave Encashment', path: '/encashment', icon: 'Banknote' },
      // { title: 'Recurring Leave', path: '/recurring-leave', icon: 'Repeat' },
      // { title: 'Year-End Processing', path: '/year-end-processing', icon: 'CalendarCog', roles: ROLES.HR_AND_ADMIN },
      { title: 'Leave Statements', path: '/leave-statements', icon: 'FileText', roles: ROLES.HR_ONLY },
      // Leave Balances accessible via Leave Statements top-banner button (removed from sidebar).
      { title: 'Absence Analytics', path: '/absence-analytics', icon: 'BarChart3', roles: ROLES.HR_ONLY },
    ]
  },
  {
    id: 'people',
    title: 'Core HR',
    icon: 'Users',
    color: 'from-amber-500 to-orange-600',
    roles: [...ROLES.MANAGER, 'Admin'],
    pages: [
      { title: 'Employee Directory', path: '/core-hr/directory', icon: 'Users' },
      { title: 'Org Structure', path: '/core-hr/org-structure', icon: 'Network' },
      { title: 'Employee Grouping', path: '/core-hr/grouping', icon: 'Users2', roles: ROLES.HR_AND_ADMIN },
      { title: 'Skills Overview', path: '/core-hr/skills-overview', icon: 'Award' },
      // Coming Soon
      { title: 'Employee Document Center', path: '/employee-document-center', icon: 'FileText', comingSoon: true },
      { title: 'Employment Lifecycle', path: '/employment-lifecycle', icon: 'RefreshCw', roles: ROLES.HR_ONLY },
      { title: 'Departments & Locations', path: '/departments-locations', icon: 'MapPin' },
      { title: 'Job Titles & Grades', path: '/job-titles-grades', icon: 'GraduationCap', comingSoon: true },
      { title: 'Company Files & Policies', path: '/company-files-hr-policies', icon: 'FolderOpen', comingSoon: true },
      { title: 'Dependents Directory', path: '/core-hr/dependents-directory', icon: 'Users', roles: ROLES.HR_ONLY },
      { title: 'Assets Management', path: '/core-hr/assets-management', icon: 'Laptop', roles: ROLES.HR_ONLY },
      { title: 'Document Template Manager', path: '/document-template-manager', icon: 'FileEdit', roles: ROLES.HR_AND_ADMIN },
      { title: 'Team Role Management', path: '/core-hr/team-role-management', icon: 'UserCog' },
      { title: 'HR Process Automation', path: '/hr-process-automation', icon: 'Cog', comingSoon: true },
      { title: 'Core HR Reports', path: '/core-hr/reports', icon: 'PieChart', roles: ROLES.HR_AND_ADMIN },
      { title: 'Bulk Document Upload', path: '/core-hr/bulk-document-upload', icon: 'Upload', roles: ROLES.HR_AND_ADMIN },
    ]
  },
  {
    id: 'recruitment',
    title: 'Recruitment',
    icon: 'UserPlus',
    color: 'from-sky-500 to-blue-600',
    roles: [...ROLES.HR_AND_ADMIN],
    pages: [
      { title: 'Dashboard', path: '/recruitment-dashboard', icon: 'LayoutDashboard' },
      { title: 'Job Openings', path: '/recruitment/job-openings', icon: 'Briefcase' },
      { title: 'Candidates', path: '/recruitment/candidates', icon: 'Users' },
      { title: 'Hiring Pipeline', path: '/recruitment/pipeline', icon: 'Kanban' },
      { title: 'Interviews', path: '/recruitment/interviews', icon: 'CalendarCheck' },
      { title: 'Offers', path: '/recruitment/offers', icon: 'FileCheck' },
      { title: 'Recruitment Settings', path: '/settings/recruitment', icon: 'Settings', roles: ROLES.HR_ONLY },
    ]
  },
  {
    id: 'impact',
    title: 'Employee Impact',
    icon: 'TrendingUp',
    color: 'from-pink-500 to-rose-500',
    pages: [],
    subModules: [
      {
        id: 'impact-engagement',
        title: 'Engagement',
        icon: 'Heart',
        pages: [
          { title: 'Dashboard', path: '/employee-impact/engagement-dashboard', icon: 'LayoutDashboard', roles: ROLES.MANAGER },
          { title: 'Recognition', path: '/employee-impact/recognition-and-feedback', icon: 'Heart' },
          { title: 'eNPS', path: '/employee-impact/enps', icon: 'BarChart3' },
          { title: 'Reflections', path: '/employee-impact/reflections', icon: 'BookOpen' },
          { title: 'Team Hub', path: '/employee-impact/team-hub', icon: 'Users', roles: ROLES.MANAGER },
          { title: 'Team Reflections', path: '/employee-impact/team-reflections', icon: 'UserCheck', roles: ROLES.MANAGER },
          { title: 'HR eNPS Analytics', path: '/hr-enps-analytics', icon: 'BarChart3', roles: ROLES.HR_AND_ADMIN },
          { title: 'Check-ins', path: '/check-ins-hub', icon: 'MessageSquare' },
        ]
      },
      {
        id: 'impact-performance',
        title: 'Performance',
        icon: 'Target',
        pages: [
          { title: 'Dashboard', path: '/employee-impact/performance-dashboard', icon: 'LayoutDashboard' },
          { title: 'Goals', path: '/employee-impact/goals', icon: 'Target' },
          { title: 'Performance Snapshot', path: '/performance-snapshot', icon: 'FileSearch', roles: ROLES.HR_AND_ADMIN },
          { title: 'Behavior & Values', path: '/behavior-and-values', icon: 'Heart', comingSoon: true },
          { title: 'AI Impact Analysis', path: '/ai-impact-analysis', icon: 'Cpu' },
        ]
      },
    ]
  },
  {
    id: 'profile',
    title: 'Profile',
    icon: 'User',
    color: 'from-indigo-500 to-blue-600',
    pages: [
      { title: 'My Profile', path: '/profile/:userId/personal', icon: 'User' },
      { title: 'Public Profile', path: '/public-profile-display', icon: 'Globe' },
      { title: 'Profile Configuration', path: '/profile-configuration-center', icon: 'Settings' },
    ]
  },
  {
    id: 'development',
    title: 'Development',
    icon: 'BookOpen',
    color: 'from-violet-500 to-purple-600',
    pages: [
      { title: 'My Development', path: '/employee-development-hub', icon: 'BookOpen', comingSoon: true },
      { title: 'Create Objective', path: '/development-objectives-creator', icon: 'Target', comingSoon: true },
      { title: 'Objective Details', path: '/development-objective-detail', icon: 'Eye', comingSoon: true },
      { title: 'Team Dashboard', path: '/team-development-dashboard', icon: 'LayoutDashboard', comingSoon: true },
      { title: 'Mentorship', path: '/employee-impact/mentorship', icon: 'GraduationCap' },
    ]
  },
  {
    id: 'payroll',
    title: 'Payroll',
    icon: 'DollarSign',
    color: 'from-emerald-500 to-green-600',
    roles: ROLES.CLOCKING,
    pages: [
      { title: 'Dashboard', path: '/payroll/dashboard', icon: 'BarChart3', roles: ROLES.HR_ONLY },
      { title: 'Payroll Table', path: '/payroll/table', icon: 'Table', roles: ROLES.HR_ONLY },
      { title: 'My Claims', path: '/payroll/my-claims', icon: 'Receipt' },
      { title: 'Claims Management', path: '/payroll/claims', icon: 'ClipboardList', roles: ROLES.HR_ONLY },
      { title: 'Payslips', path: '/payroll/payslips', icon: 'FileText', roles: ROLES.HR_ONLY },
      // Coming Soon
      { title: 'Payroll Run', path: '/payroll-run-wizard', icon: 'Play', comingSoon: true },
      { title: 'Payroll Documents', path: '/payroll-documents-center', icon: 'FileText', comingSoon: true },
      { title: 'Payroll Reports', path: '/payroll-reports', icon: 'PieChart', roles: ROLES.HR_AND_ADMIN },
      { title: 'GOSI Contributions', path: '/payroll/pension', icon: 'Landmark', roles: ROLES.HR_ONLY },
      { title: 'Compliance Center', path: '/payroll/compliance-center', icon: 'ShieldCheck', roles: ROLES.HR_ONLY },
      { title: 'WPS Center', path: '/payroll/wps-center', icon: 'FileSpreadsheet', roles: ROLES.HR_ONLY },
      { title: 'EOS', path: '/eos-center', icon: 'UserMinus' },
    ]
  },
  {
    id: 'tournament',
    title: 'Tournament',
    icon: 'Trophy',
    color: 'from-yellow-500 to-amber-600',
    pages: [
      { title: 'Tournaments', path: '/tournament-management/tournaments', icon: 'Trophy' },
      { title: 'Matches', path: '/tournament-management/matches', icon: 'Swords' },
      { title: 'Referees', path: '/tournament-management/referees', icon: 'Users', roles: ROLES.HR_AND_ADMIN },
      { title: 'Referee Assignment', path: '/tournament-management/referee-assignment', icon: 'UserCog', roles: ROLES.HR_AND_ADMIN },
      { title: 'My Assignments', path: '/tournament-management/my-assignments', icon: 'ClipboardList' },
      { title: 'Work Points', path: '/tournament-management/work-points', icon: 'Calculator', roles: ROLES.HR_AND_ADMIN },
      { title: 'Compensation', path: '/tournament-management/compensation', icon: 'DollarSign', roles: ROLES.HR_AND_ADMIN },
      { title: 'Referee Workload', path: '/tournament-management/referee-workload', icon: 'BarChart3', roles: ROLES.HR_AND_ADMIN },
      { title: 'Marketplace', path: '/tournament-management/marketplace', icon: 'ShoppingBag', comingSoon: true }
    ]
  },
  {
    id: 'projects',
    title: 'Projects',
    icon: 'FolderKanban',
    color: 'from-teal-500 to-emerald-600',
    pages: [
      { title: 'Projects List', path: '/projects-list', icon: 'Briefcase', comingSoon: true },
      { title: 'Task Board', path: '/task-board', icon: 'ClipboardList', comingSoon: true },
    ]
  },
  {
    id: 'settings',
    title: 'Settings',
    icon: 'Settings',
    color: 'from-gray-500 to-slate-600',
    isAdmin: true,
    roles: ROLES.SETTINGS,
    pages: [
      // Development (flat — Operator-only dev tooling, distinct from product config)
      { title: 'Module Config', path: '/settings/module-config', icon: 'LayoutGrid', roles: ROLES.OPERATOR, group: 'Development' },
      { title: 'AI Settings', path: '/settings/ai-settings', icon: 'Bot', roles: ROLES.OPERATOR, group: 'Development' },
      { title: 'Seeding (Dev)', path: '/settings/development/seeding', icon: 'Database', roles: ROLES.OPERATOR, group: 'Development' },
      { title: 'Blob Migration', path: '/settings/blob-migration', icon: 'DatabaseBackup', roles: ROLES.OPERATOR, group: 'Development' },
    ],
    // Sub-modules mirror the product modules they configure.
    subModules: [
      {
        id: 'settings-general',
        title: 'General',
        icon: 'Building2',
        pages: [
          { title: 'General Details', path: '/settings/general-details', icon: 'Building2' },
          { title: 'Companies', path: '/settings/companies', icon: 'Building', roles: ROLES.OPERATOR },
          { title: 'Subscriptions', path: '/settings/operator/subscriptions', icon: 'CreditCard', roles: ROLES.OPERATOR },
          { title: 'Platform Terms', path: '/settings/operator/platform-terms', icon: 'FileText', roles: ROLES.OPERATOR },
          { title: 'Company Rules', path: '/settings/operator/company-rules', icon: 'ScrollText', roles: ROLES.OPERATOR },
          { title: 'Mobile App Version', path: '/settings/operator/app-version', icon: 'Smartphone', roles: ROLES.OPERATOR },
          { title: 'Organizations', path: '/settings/organizations', icon: 'Network', roles: ROLES.OPERATOR },
          { title: 'Branches & Departments', path: '/settings/branches-and-departments', icon: 'GitBranch' },
          { title: 'Currency & Banks', path: '/settings/currency-and-banks', icon: 'Banknote' },
          { title: 'Attachment Types', path: '/settings/general/attachment-types', icon: 'Paperclip' },
          { title: 'Date Formats', path: '/settings/general/date-formats', icon: 'CalendarDays', roles: ROLES.OPERATOR },
          { title: 'System Emails', path: '/settings/system-emails', icon: 'Mail' },
          { title: 'Files Configuration', path: '/settings/general/file-upload-settings', icon: 'FileUp', roles: ROLES.OPERATOR },
          { title: 'OCR Configuration', path: '/settings/general/ocr-settings', icon: 'ScanLine' },
          { title: 'Terms & Policies', path: '/settings/company/terms-and-policies', icon: 'FileText', roles: ROLES.HR_ONLY },
          { title: 'Notification Management', path: '/settings/notification-management', icon: 'BellRing', roles: ROLES.SETTINGS },
        ]
      },
      {
        id: 'settings-attendance',
        title: 'Time & Attendance',
        icon: 'Clock',
        pages: [
          { title: 'Attendance Rules', path: '/settings/attendance/work-schedule-config', icon: 'Shield' },
          { title: 'Holidays', path: '/settings/attendance/holidays', icon: 'PartyPopper' },
          { title: 'Workplace Map', path: '/settings/attendance/workplace-map', icon: 'MapPin' },
          { title: 'Face Recognition', path: '/settings/attendance/face-recognition', icon: 'ScanFace', roles: ROLES.OPERATOR },
          { title: 'Wialon Settings', path: '/settings/attendance/wialon', icon: 'Navigation', roles: ROLES.OPERATOR },
        ]
      },
      {
        id: 'settings-leaves',
        title: 'Leaves',
        icon: 'Palmtree',
        pages: [
          { title: 'Leave Settings & Policies', path: '/leave-policies', icon: 'Settings2', roles: ROLES.HR_ONLY },
          { title: 'Statutory Templates', path: '/statutory-leave-templates', icon: 'Scale', roles: ROLES.HR_ONLY },
        ]
      },
      {
        id: 'settings-payroll',
        title: 'Payroll',
        icon: 'DollarSign',
        pages: [
          { title: 'Gross Pays', path: '/settings/payroll/gross-pays', icon: 'DollarSign' },
          { title: 'Claims Settings', path: '/settings/payroll/claims', icon: 'Receipt', roles: ROLES.HR_ONLY },
          { title: 'Variable Pays', path: '/settings/payroll/variable-pays', icon: 'TrendingUp', roles: ROLES.HR_ONLY },
          { title: 'Benefits', path: '/settings/payroll/benefits', icon: 'Gift', roles: ROLES.HR_ONLY },
          { title: 'End of Service', path: '/settings/payroll/end-of-service', icon: 'UserMinus', roles: ROLES.HR_ONLY },
          { title: 'Payslips Config', path: '/settings/payroll/payslips', icon: 'FileText' },
          { title: 'Compliance Config', path: '/settings/payroll/compliance', icon: 'ShieldCheck', roles: ROLES.HR_AND_OPERATOR },
        ]
      },
      {
        id: 'settings-people',
        title: 'Core HR',
        icon: 'Users',
        pages: [
          { title: 'Welcome Emails', path: '/core-hr/welcome-emails', icon: 'Mail', roles: ROLES.HR_AND_ADMIN },
          { title: 'Lifecycle Settings', path: '/settings/lifecycle', icon: 'Settings2' },
          { title: 'Asset Management', path: '/settings/core-hr/asset-management', icon: 'Laptop', roles: ROLES.SETTINGS },
          { title: 'Core HR Settings', path: '/settings/core-hr-settings', icon: 'Settings', comingSoon: true },
        ]
      },
      {
        id: 'settings-impact',
        title: 'Employee Impact',
        icon: 'TrendingUp',
        pages: [
          { title: 'Recognition Privacy', path: '/settings/employee-impact/recognition-privacy', icon: 'Lock' },
          { title: 'Recognition Values', path: '/settings/employee-impact/recognition-values', icon: 'Heart' },
        ]
      },
      {
        id: 'settings-projects',
        title: 'Projects',
        icon: 'FolderKanban',
        pages: [
          { title: 'Projects', path: '/settings/project-management/projects', icon: 'FolderKanban' },
        ]
      },
      {
        id: 'settings-profile',
        title: 'Profile',
        icon: 'User',
        pages: [
          { title: 'Roles & Authorization', path: '/profile-authorization-settings', icon: 'Shield' },
          { title: 'Profile Settings', path: '/profile-settings', icon: 'User' },
          { title: 'Public Profile Settings', path: '/hr-admin-public-profile-settings', icon: 'Globe' },
        ]
      },
    ]
  }
];

/**
 * Look up a navigation page's (English) title by its route path.
 *
 * Used to localize assistant navigation deep-links: the returned title is a
 * translation key that goes through `t()`, so a route the assistant hands back
 * ("/attendance/team-attendance") renders with the same label as the sidebar.
 * Returns `undefined` when no page matches (caller falls back to the
 * backend-provided label). Trailing slashes and query/hash are ignored.
 */
export function findNavPageTitleByPath(path: string): string | undefined {
  if (!path) return undefined;
  const normalize = (p: string) => {
    const clean = p.split('?')[0].split('#')[0];
    return clean.length > 1 ? clean.replace(/\/+$/, '') : clean;
  };
  const target = normalize(path);
  for (const module of navigationModules) {
    for (const page of module.pages) {
      if (normalize(page.path) === target) return page.title;
    }
    for (const sub of module.subModules ?? []) {
      for (const page of sub.pages) {
        if (normalize(page.path) === target) return page.title;
      }
    }
  }
  return undefined;
}

/**
 * Mobile-specific module and page configuration.
 * These IDs are used by the Flutter mobile app.
 * Operators can toggle them per company via the ModuleConfig settings page.
 */

export interface MobileModule {
  id: string;           // moduleId stored in CompanyModuleConfig
  title: string;        // Display label
  icon: string;         // Lucide icon name
  color: string;        // Tailwind gradient
  pages: MobilePage[];  // Sub-pages within this module
}

export interface MobilePage {
  id: string;          // pagePath value stored in CompanyPageConfig
  title: string;       // Display label
  icon: string;        // Lucide icon name
  alwaysEnabled?: boolean;
}

export const mobileModules: MobileModule[] = [
  {
    id: 'mobile-attendance',
    title: 'Attendance',
    icon: 'Clock',
    color: 'from-blue-500 to-indigo-600',
    pages: [
      { id: 'mobile/attendance/clocking', title: 'Clocking', icon: 'Fingerprint' },
      { id: 'mobile/attendance/timesheet', title: 'Timesheet', icon: 'Timer' },
      { id: 'mobile/attendance/schedule', title: 'Schedule', icon: 'CalendarDays' },
      { id: 'mobile/attendance/maps', title: 'Maps', icon: 'MapPin' },
    ],
  },
  {
    id: 'mobile-leaves',
    title: 'Time-off',
    icon: 'Palmtree',
    color: 'from-green-500 to-emerald-600',
    pages: [
      { id: 'mobile/leaves/balances', title: 'Balances', icon: 'Scale' },
      { id: 'mobile/leaves/leaves', title: 'Leaves', icon: 'CalendarDays' },
      { id: 'mobile/leaves/holidays', title: 'Holidays', icon: 'Palmtree' },
      { id: 'mobile/leaves/statements', title: 'Statements', icon: 'FileText' },
      { id: 'mobile/leaves/team-calendar', title: 'Team Calendar', icon: 'CalendarRange' },
    ],
  },
  {
    id: 'mobile-employees',
    title: 'Employees',
    icon: 'Users',
    color: 'from-amber-500 to-orange-600',
    pages: [
      { id: 'mobile/employees/list', title: 'Employee List', icon: 'Users', alwaysEnabled: true },
    ],
  },
  {
    id: 'mobile-payroll',
    title: 'Payroll',
    icon: 'Banknote',
    color: 'from-emerald-500 to-green-600',
    pages: [
      { id: 'mobile/payroll/dashboard', title: 'Dashboard', icon: 'LayoutDashboard' },
      { id: 'mobile/payroll/my-claims', title: 'My Claims', icon: 'CalendarDays' },
      { id: 'mobile/payroll/claims', title: 'Claims', icon: 'Timer' },
    ],
  },
  {
    id: 'mobile-performance',
    title: 'Employee Impact',
    icon: 'TrendingUp',
    color: 'from-pink-500 to-rose-500',
    pages: [
      { id: 'mobile/performance/dashboard', title: 'Dashboard', icon: 'LayoutDashboard' },
      { id: 'mobile/performance/feedback', title: 'Continuous Feedback', icon: 'MessageSquare' },
    ],
  },
  {
    id: 'mobile-tournament',
    title: 'Tournaments',
    icon: 'Trophy',
    color: 'from-yellow-500 to-amber-600',
    pages: [
      { id: 'mobile/tournaments', title: 'Tournaments', icon: 'Trophy' },
      { id: 'mobile/matches', title: 'Matches', icon: 'Swords' },
      { id: 'mobile/referee-assignment', title: 'Referee Assignment', icon: 'UserCog' },
      { id: 'mobile/referee-management', title: 'Referee Management', icon: 'UserCog' },
      { id: 'mobile/my-assignments', title: 'My Assignments', icon: 'ClipboardList' },
      { id: 'mobile/work-points', title: 'Work Points', icon: 'Calculator' },
    ],
  },
];

// NOTE: `mobile-assignments` and `mobile-goals` are intentionally NOT registered as standalone modules.
// My Assignments is a sub-page of the Tournaments module (toggle via `mobile/my-assignments` page).
// Goals has no dedicated screen yet — add it here when the Goals feature is built.

// Backwards compatibility — keep old exports for existing code that references them
export const MOBILE_MODULE_ID = 'mobile-tournament';
export const mobileTournamentPages = mobileModules.find(m => m.id === 'mobile-tournament')!.pages;
